<template>
  <div>
    <Header
      :title="$t('queues.queues').toString()"
      :items="headerButtons"
      @create-queue="$router.push({ name: 'projectSettingsQueuesCreate' })"
    />
    <template v-if="!isPresetProject && !instance?.isPresetInstance">
      <div v-if="limitReached" class="alert alert-danger">
        {{ $t("queues.limitReached") }}
      </div>
      <div v-else-if="limit > 0" class="alert alert-warning">
        {{ $t("queues.usage", { usage: usage, limit: limit }) }}
      </div>
    </template>
    <TableHelper
      ref="table"
      :items="queues"
      :meta="meta"
      :fields="fields"
      :actions="actions"
      :filterable="filterable"
      :enable-filter="true"
      default-order-by="name"
      loading-key="loadSettingsQueues"
      @reload-data="reloadData"
      @edit="showDetails"
      @delete="deleteQueue"
    />
  </div>
</template>

<script>
import Swal from "sweetalert2";
import Queues from "@/components/Settings/Queues/queues";
import { mapGetters } from "vuex";
import TableHelper from "@/components/Tools/TableHelper/TableHelper.vue";
import Header from "@/components/Tools/Header/Header.vue";
import {
  addEventToLoadingQueue,
  removeEventFromLoadingQueue
} from "@/composables/useLoadingQueue";
import { useCurrentProjectFilter } from "@/composables/useCurrentProjectFilter";
import RateLimit from "@/components/RateLimit/rateLimit";

export default {
  name: "ProjectsSettingsQueuesTable",
  components: {
    Header,
    TableHelper
  },
  data() {
    return {
      tempFilterable: {},
      queues: [],
      searchTitle: "",
      fields: [
        {
          key: "raw_name",
          label: this.$t("queues.name"),
          sortable: true
        },
        {
          key: "max_parallel_jobs",
          label: this.$t("queues.maxParallelJobs"),
          sortable: true
        },
        {
          key: "active",
          label: this.$t("general.status"),
          type: "activeBadge",
          sortable: true
        },
        {
          key: "updated_at",
          label: this.$t("table.updatedAt"),
          sortable: true,
          type: "datetime"
        },
        {
          key: "created_at",
          label: this.$t("table.createdAt"),
          sortable: true,
          type: "datetime"
        }
      ],
      meta: {},
      rateLimit: null
    };
  },
  computed: {
    ...mapGetters("route", ["params"]),
    ...mapGetters("instance", ["instance"]),
    ...mapGetters([
      "selectedProject",
      "selectedPresetVersion",
      "isDevPresetVersion",
      "isPresetProject"
    ]),
    filterable() {
      return this.tempFilterable;
    },
    headerButtons: function () {
      if (!this.isDevPresetVersion || !this.rateLimit || this.limitReached) {
        return [];
      }
      return [
        {
          type: "button",
          title: this.$t("queues.queueCreate"),
          icon: "fa-light fa-plus",
          emit: "create-queue"
        }
      ];
    },
    actions: function () {
      const actions = [
        {
          key: "edit",
          icon: "fal fa-pen",
          emit: "edit",
          tooltip: this.$t("general.edit")
        }
      ];
      if (this.isDevPresetVersion) {
        actions.push({
          key: "delete",
          icon: "fal fa-trash",
          emit: "delete",
          tooltip: this.$t("general.delete")
        });
      }
      return actions;
    },
    limit: function () {
      return this.rateLimit?.limits?.workers ?? 0;
    },
    usage: function () {
      return this.rateLimit?.usages?.workers ?? 0;
    },
    limitReached: function () {
      if (this.limit <= 0) return false;
      return this.limit <= this.usage;
    }
  },
  watch: {
    selectedProject: function () {
      this.loadQueues();
    },
    selectedPresetVersion: function () {
      this.loadQueues();
    }
  },
  mounted() {
    this.reloadData();
  },
  methods: {
    loadRateLimits() {
      RateLimit.get().then(response => {
        this.rateLimit = response;
      });
    },
    reloadData() {
      this.loadQueues();
      this.loadRateLimits();
    },
    loadQueues(data) {
      addEventToLoadingQueue({ key: "loadSettingsQueues" });

      let params = this.params();
      const { filter } = useCurrentProjectFilter();

      Queues.getAll(data ?? params, filter.value)
        .then(response => {
          this.queues = response.data;
          this.meta = response.meta;
          this.tempFilterable = response.meta.filterable;
          removeEventFromLoadingQueue({ key: "loadSettingsQueues" });
        })
        .catch(error => {
          this.$error(error);
        });
    },
    showDetails(item) {
      this.$router.push({
        name: "projectSettingsQueuesEdit",
        params: { queueId: item.item.id }
      });
    },
    deleteQueue(item) {
      Swal.fire({
        title: this.$t("queues.deleteConfirm", { queue: item.item.raw_name }),
        icon: "warning",
        showConfirmButton: true,
        confirmButtonText: this.$t("general.remove"),
        confirmButtonColor: "var(--primary)",
        showCancelButton: true,
        reverseButtons: true,
        cancelButtonText: this.$t("general.cancel")
      }).then(result => {
        if (result.isConfirmed) {
          addEventToLoadingQueue({ key: "deleteQueue" });
          Queues.delete(item.item.id)
            .then(() => {
              removeEventFromLoadingQueue({
                key: "deleteQueue",
                type: "success",
                prefix: "queues",
                name: "deleteSuccessText",
                data: { queue: item.item.raw_name }
              });
              this.loadQueues();
              this.loadRateLimits();
            })
            .catch(error => {
              this.$error(error);
            });
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
.alert {
  background: white;
}
</style>
